import styled from 'styled-components';
import MaskedInput from 'react-text-mask';

import { WithThemeProps, pxToRem } from '../../styles';

import { DateRangePickerProps, RangeInputProps } from './date-range-picker';


type RootProps = {

} & DateRangePickerProps['RootProps'];

type PeriodNameProps = {
	marginLeft?: number;
}

type RangeInputStyledProps = {
	readOnly: boolean;
} & WithThemeProps & RangeInputProps;

const Root = styled.div<RootProps>`
	display: inline-flex;
	position: relative;
	font-family: Roboto, sans-serif;

	${p => p.fullWidth && `
		width: 100%;
	`}
`;

const TriggerLayout = styled.div`
	display: block;
	width: auto;
	height: auto;
`;

const DateRangeLayout = styled.div`
	position: relative;
	display: inline-block;
	font-family: Roboto, sans-serif;
	width: auto;
	height: auto;
	min-height: 305px;
	background-color: #fff;
	padding: 32px 16px;

	& .rdr-Day {
		transform: scale(1) !important;
	}

	& .rdr-PredefinedRangesItem:hover {
		color: ${(p: WithThemeProps) => p.theme.color.accentPrimary} !important;
	}

	& .rdr-PredefinedRangesItemActive:hover {
		color: #fff !important;
	}

	${(p: WithThemeProps) => `
		& .rdr-Day.is-inRange {
			background-color: ${p.theme.color.accentPrimary} !important;
		}

		& .rdr-Day.is-selected {
			background-color: ${p.theme.color.accentPrimaryPressed} !important;
		}

		& .rdr-PredefinedRangesItem {
			color: ${p.theme.color.blackSecondary} !important;
		}

		& .rdr-PredefinedRangesItemActive {
			background: ${p.theme.color.accentPrimary} !important;
			color: #fff !important;
		}

		& .rdr-Day:hover {
			background-color: ${p.theme.color.accentPrimaryHover} !important;
		}
	`}
`;

const EnterDateLayout = styled.div`
	position: absolute;
	bottom: 30px;
	left: 37px;
	width: 170px;
	display: inline-flex;
	flex-flow: column nowrap;
	justify-content: flex-start;
	align-items: flex-start;
`;

const EnterDateSign = styled.div`
	font-size: 14px;
	font-weight: 500;
	text-align: left;
	color: ${(p: WithThemeProps) => p.theme.color.blackPrimary};
	margin-bottom: 12px;
`;

const EnterDateInputsLayout = styled.div`
	display: flex;
`;

const EnterDateSeparator = styled.span`
	margin: 0 4px;
	color: ${(p: WithThemeProps) => p.theme.color.blackPrimary};
`;

const DividerLayout = styled.div`
	position: absolute;
	top: 28px;
	left: 216px;
	z-index: 100;
`;

const Divider = styled.div`
	width: 1px;
	height: 285px;
	background-color: ${(p: WithThemeProps) => p.theme.color.blackStealthy};
`;

const TriggerInputLayout = styled.div`
	display: flex;
	flex-flow: row nowrap;
	justify-content: flex-start;
	align-items: center;
	line-height: 1.2;
	font-size: 24px;
	border-bottom: 1px dotted ${(p: WithThemeProps) => p.theme.color.blackPrimary};

	&:hover {
		border-color: ${(p: WithThemeProps) => p.theme.color.accentPrimary};
	}

	&:hover * {
		color: ${(p: WithThemeProps) => p.theme.color.accentPrimary} !important;
	}

	&:hover use {
		fill: ${(p: WithThemeProps) => p.theme.color.accentPrimary} !important;
	}

	& hr {
		display: none !important;
	}

	* {
		font-size: 24px;
	}

	@media(max-width: 1400px) {
		font-size: 20px;

		* {
			font-size: 20px;
		}
	}
`;

const PeriodName = styled.span<PeriodNameProps>`
	font-weight: 500;

	${p => p.marginLeft && `
		margin-left: ${p.marginLeft}px;
	`}
`;

const PeriodNameLayout = styled.div`
	flex-shrink: 0;
	cursor: pointer;
`;

const DropdownLayout = styled.div`
	display: flex;
	flex-flow: row nowrap;
	align-items: center;
	cursor: pointer;
`;

const RangeInputStyled = styled(MaskedInput)`
	position: relative;
	font-family: Roboto, arial;
	z-index: 1;
	background-color: transparent;
	border: 0;
	outline: none;
	font-size: inherit;
	font-weight: 400;
	padding: 0;

	${(p: RangeInputStyledProps) => p.appearance === 'form-control' && `
		color: ${p.theme.palette.accentPrimary};
		border-bottom: 1px dotted ${p.theme.palette.accentPrimary};
	`}

	${(p: RangeInputStyledProps) => p.disabled && `
		color: ${p.theme.palette.blackDisabled};
		border-bottom: 1px dotted ${p.theme.palette.blackDisabled};
	`}

	${(p: RangeInputStyledProps) => p.readOnly && `
		color: ${p.theme.palette.blackPrimary};
		border-bottom: 1px dotted ${p.theme.palette.blackPrimary};
	`}
`;

const DateInputStyled = styled(MaskedInput)`
	width: 100%;
	font-family: Roboto, arial;
	background-color: #fff;
	outline: none;
	border: 0;
	font-size: 14px;
	transition: border-color .2s ease-in-out;
	line-height: 1.4;

	${(p: WithThemeProps) => `
		border-bottom: 1px solid ${p.theme.color.blackDisabled};
	`};

	:focus {
		border-bottom: 1px solid ${(p: WithThemeProps) => p.theme.color.accentPrimary};
	}
`;

const createDateRangeTheme = () => {
	return {
		DateRange: {
			display: 'inline-flex',
			flexFlow: 'row nowrap',
			justifyContent: 'flex-start',
			alignItems: 'flex-start',
			width: 'auto',
			maxWidth: '900px',
			borderRadius: '0',
			background: 'transparent'
		},
		Calendar: {
			order: '2',
			width: '268px',
			padding: '0 15px 0 15px'
		},
		Day: {
			width: '34px',
			height: '34px',
			fontWeight: '400',
			fontSize: '12px',
			lineHeight: '1.25',
			background: '#fff',
			display: 'inline-flex',
			flexFlow: 'row wrap',
			justifyContent: 'center',
			alignItems: 'center',
		},
		Weekday: {
			width: '34px',
			height: '34px',
			fontSize: '12px',
			fontWeight: '400',
			lineHeight: '1.25',
			marginBottom: '0',
			display: 'inline-flex',
			flexFlow: 'row wrap',
			justifyContent: 'center',
			alignItems: 'center',
		},
		DayHover: {
			color: '#fff'
		},
		DaySelected: {
			color: '#fff'
		},
		DayInRange: {
			color: '#fff',
		},
		DayStartEdge: {},
		DayEndEdge: {},
		PredefinedRanges: {
			position: 'relative',
			top: '3px',
			left: 'auto',
			right: '12px',
			order: '1',
			display: 'flex',
			flexFlow: 'column wrap',
			justifyContent: 'flex-start',
			alignItems: 'flex-start',
			width: 'auto',
			height: 'auto',
			backgroundColor: '#fff',
			padding: '0 24px 24px 24px',
			margin: '0'
		},
		PredefinedRangesItem: {
			width: '100%',
			background: 'transparent',
			marginBottom: '0',
			padding: '8px 10px',
			textAlign: 'left',
			fontSize: 14,
			lineHeight: 1.25,
		},
		PredefinedRangesItemActive: {
			color: '#fff'
		},
		MonthAndYear: {
			fontSize: '14px',
			fontWeight: '500',
			lineHeight: '1.25',
		},
		MonthButton: {
			background: 'transparent',
			cursor: 'pointer'
		},
		MonthArrowPrev: {
			borderRightColor: 'rgba(0, 0, 0, 0.85)',
			position: 'relative',
			left: '-4px'
		},
		MonthArrowNext: {
			borderLeftColor: 'rgba(0, 0, 0, 0.85)',
			position: 'relative',
			right: '-4px'
		}
	};
};

const DateRangePickerTitleRoot = styled.div`
	margin-bottom: 10px;
`;

const DateRangePickerTitleText = styled.h1`
	display: inline;
	margin: 0;
	font-size: ${pxToRem(24)};
	font-weight: 400;

	@media(max-width: 1400px) {
		font-size: ${pxToRem(20)};
	}
`;

const FormControlLayout = styled.span`
	position: relative;
	display: inline-block;
	font-size: ${pxToRem(14)};
`;

const Hidden = styled.span`
	position: absolute;
	visibility: hidden;
`;

export {
	Root,
	TriggerLayout,
	DateRangeLayout,
	EnterDateLayout,
	EnterDateSign,
	EnterDateInputsLayout,
	EnterDateSeparator,
	DividerLayout,
	Divider,
	TriggerInputLayout,
	PeriodName,
	PeriodNameLayout,
	DropdownLayout,
	RangeInputStyled,
	DateInputStyled,
	createDateRangeTheme,
	DateRangePickerTitleRoot,
	DateRangePickerTitleText,
	FormControlLayout,
	Hidden,
};
